import { Col, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCircleFilled } from '@ant-design/icons';
import { NO_CHIP_MINIMIZER } from '@common/constants';
import { DrawingShape, SelectedItemMap } from '@common/models';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    resetSelectedChipMinimizers,
    selectSelectedChipMinimizers,
    updateSelectedChipMinimizers,
} from '@redux/slices/chipMinimizerSlice';

import ChipMinimizerItem from './ChipMinimizerItem';
import { showChipMinimizer as hasChipMinimizer } from './ChipMinimizerSection.utils';

import './ChipMinimizerSection.scss';

const ChipMinimizer = (): JSX.Element | null => {
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showChipMinimizer, setShowChipMinimizer] = useState(false);
    const { drawingShapes } = useContext(DrawingShapeContext);

    const selectedChipMinimizers = useAppSelector(selectSelectedChipMinimizers);
    const { chipMinimizer } = useOptionalItems();
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (chipMinimizer) {
            const appliances = drawingShapes.flatMap((drawingShape: DrawingShape) => drawingShape.appliances);
            const shouldShowChipminimizer = hasChipMinimizer(appliances);
            setShowChipMinimizer(shouldShowChipminimizer);
            if (!shouldShowChipminimizer) {
                dispatch(resetSelectedChipMinimizers());
            }
        }
    }, [drawingShapes, chipMinimizer, dispatch]);

    const handleChipMinimizerClick = (productId: number) => {
        const newSelectedChipminimizers: SelectedItemMap = new Map();
        newSelectedChipminimizers.set(productId, { isSelected: true, isManuallyChanged: false, quantity: 1 });
        dispatch(updateSelectedChipMinimizers(newSelectedChipminimizers));
    };

    const handleQuantityChange = (productId: number, value: number) => {
        const newSelectedChipminimizers: SelectedItemMap = new Map();
        newSelectedChipminimizers.set(productId, {
            isSelected: value > 0,
            isManuallyChanged: false,
            quantity: value,
        });
        dispatch(updateSelectedChipMinimizers(newSelectedChipminimizers));
    };

    if (!chipMinimizer || !showChipMinimizer) {
        return null;
    }

    return (
        <>
            <p>
                <span className="sub-description">{t('CHIP_MINIMIZER.TITLE')}</span>
                <InfoCircleFilled className="ChipMinimizerSection__info-icon" onClick={() => setShowInfoModal(true)} />
            </p>
            <Row gutter={20}>
                <Col className="ChipMinimizerSection__grid_item">
                    <ChipMinimizerItem
                        item={chipMinimizer}
                        quantity={selectedChipMinimizers.get(chipMinimizer.productId)?.quantity}
                        isSelected={!!selectedChipMinimizers.get(chipMinimizer.productId)?.isSelected}
                        onClick={() => handleChipMinimizerClick(chipMinimizer.productId)}
                        onQuantityChange={(value) => handleQuantityChange(chipMinimizer.productId, value)}
                        additionalClasses={{ 'multi-action-card': true }}
                    />
                </Col>
                <Col className="ChipMinimizerSection__grid_item">
                    <ChipMinimizerItem
                        item={NO_CHIP_MINIMIZER}
                        imgPath={NO_CHIP_MINIMIZER.imageUrl}
                        isSelected={!!selectedChipMinimizers.get(NO_CHIP_MINIMIZER.productId)?.isSelected}
                        onClick={() => handleChipMinimizerClick(NO_CHIP_MINIMIZER.productId)}
                        hasQuantitySelector={false}
                        additionalClasses={{ 'single-action-card': true }}
                    />
                </Col>
            </Row>

            <Modal
                open={showInfoModal}
                onCancel={() => setShowInfoModal(false)}
                width="500px"
                centered
                footer={null}
                closable
            >
                <span>{t('CHIP_MINIMIZER.INFO_CONTENT')}</span>
            </Modal>
        </>
    );
};

export default ChipMinimizer;
